<template>
  <div class="container mt-5">
    <h2>Admin Login</h2>

    <div v-if="!isAuthenticated" class="mb-3">
      <input v-model="adminHash" class="form-control" placeholder="Enter admin hash" />
      <button @click="authenticate" class="btn btn-primary mt-2">Login</button>
      <div v-if="error" class="alert alert-danger mt-2">{{ error }}</div>
    </div>

    <div v-if="isAuthenticated && !error" class="mt-4">
      <h3>All Tokens</h3>

      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Token ID</th>
            <th>Name</th>
            <th>Hash</th>
            <th>Type</th>
            <th>Expiration Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="token in allTokens" :key="token.tokenId">
            <td>{{ token.tokenId }}</td>

            <!-- Поле для имени файла/папки -->
            <td>
              {{ token.name }}
            </td>

            <!-- Поле для hash: Только для копирования, редактировать нельзя -->
            <td>
              <span class="copy-box" @click="copyToClipboard(token.hash, 'tooltip-' + token.tokenId)">
                {{ token.hash }}
              </span>
              <span :id="'tooltip-' + token.tokenId" class="tooltip-text">Copied!</span>
            </td>

            <!-- Поле для типа токена: можно редактировать только после нажатия Edit -->
            <td>
              <select v-model="token.tokenType" class="form-select" :disabled="!token.isEditing">
                <option value="SingleFile">Single File</option>
                <option value="Folder">Folder</option>
                <option value="Admin">Admin</option>
                <option value="OneTimeFile">One Time File</option>
              </select>
            </td>

            <!-- Поле для даты истечения и времени -->
            <td>
              <span v-if="!token.isEditing">{{ token.expirationDate }} {{ token.expirationTime }}</span>
              <div v-else>
                <input type="date" v-model="token.expirationDate" class="form-control mb-1" />
                <input type="time" v-model="token.expirationTime" class="form-control" />
              </div>
            </td>

            <!-- Кнопки Edit/Save и Delete -->
            <td>
              <button v-if="!token.isEditing" @click="editToken(token)" class="btn btn-primary">Edit</button>
              <button v-else @click="saveToken(token)" class="btn btn-success">Save</button>
              <button @click="deleteToken(token)" class="btn btn-danger ms-2">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Кнопка Load More -->
      <div v-if="hasMoreTokens && !loading" class="text-center mt-3">
        <button @click="loadMoreTokens" class="btn btn-secondary">Load More</button>
      </div>

      <!-- Сообщение о том, что все токены загружены -->
      <div v-if="!hasMoreTokens && !loading" class="text-muted text-center mt-3">
        <p>All tokens loaded.</p>
      </div>

      <!-- Спиннер загрузки -->
      <div v-if="loading" class="text-center mt-3">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Loading...
      </div>
    </div>
  </div>
</template>

<script>
import { loadConfig } from '../axiosRequest';
import { mapActions, mapGetters } from 'vuex';

let axiosInstance = null; // Глобальная переменная для хранения загруженного инстанса axios

async function getAxiosInstance() {
  if (!axiosInstance) {
    axiosInstance = await loadConfig(); // Загружаем только один раз
  }
  return axiosInstance;
}


export default {
  data() {
    return {
      currentPage: 1, // Текущая страница
      pageSize: 10, // Размер страницы
      loading: false, // Флаг загрузки
      hasMoreTokens: true, // Флаг наличия дополнительных токенов
      allTokens: [], // Хранение всех загруженных токенов
    };
  },
  computed: {
    ...mapGetters(['getAuthenticatedStatus', 'getAdminHash', 'getError']),
    isAuthenticated() {
      return this.getAuthenticatedStatus;
    },
    adminHash: {
      get() {
        return this.getAdminHash;
      },
      set(value) {
        this.saveAdminHash(value);
      }
    },
    error() {
      return this.getError;
    }
  },
  methods: {
    ...mapActions(['saveAuthenticatedStatus', 'saveAdminHash', 'saveError']),
    async authenticate() {
      try {
        const axios = await getAxiosInstance();
        const response = await axios.get(`/tokens/Authenticate?AdminHash=${this.adminHash}`);
        if (response.data.success) {
          this.saveAuthenticatedStatus(true);
          this.saveError(null);
          this.loadTokens(); // Загружаем первую страницу токенов
        } else {
          this.saveError('Invalid admin hash');
        }
      } catch (error) {
        // Обработка ошибки сети
        if (!error.response) {
          this.saveError('The server is unavailable. Please check your connection or try again later.');
        } else {
          this.saveError('Invalid admin hash');
        }
        console.error('Error authenticating:', error);
      }
    },
    async loadTokens() {
      this.loading = true; // Устанавливаем состояние загрузки
      try {
        const axios = await getAxiosInstance();
        const response = await axios.get(`/tokens/Page/${this.currentPage}?AdminHash=${this.adminHash}`);

        console.log(response.data.length);
        console.log(this.pageSize);
        if (response.data.length < this.pageSize) {
          this.hasMoreTokens = false; // Если токенов меньше, чем размер страницы, больше нечего загружать
        }

        // Добавляем загруженные токены к существующим
        const loadedTokens = response.data.map(token => ({
          ...token,
          isEditing: false, // Изначально режим редактирования выключен
          tokenType: token.tokenType || 'SingleFile', // Устанавливаем тип по умолчанию, если он не задан
          expirationTime: token.expirationTime || '00:00', // Устанавливаем время по умолчанию, если его нет
        }));

        this.allTokens = [...this.allTokens, ...loadedTokens];
        this.currentPage++; // Увеличиваем номер страницы для следующей загрузки
        this.loading = false; // Отключаем состояние загрузки
      } catch (error) {
        console.error('Error loading tokens:', error);
        this.saveError('Error loading tokens.');
        this.loading = false;
      }
    },
    async loadMoreTokens() {
      if (!this.loading) {
        this.loadTokens(); // Загружаем следующую страницу токенов
      }
    },
    // Активируем режим редактирования для токена
    editToken(token) {
      token.isEditing = true;
    },
    async saveToken(token) {
      try {
        const axios = await getAxiosInstance();
        const adminHash = this.adminHash;

        // Устанавливаем минимальную дату по умолчанию
        let expirationDateTime = new Date('1970-01-01T00:00:00.000Z').toISOString(); // Минимальная дата в UTC

        // Если дата указана, конвертируем её в UTC
        if (token.expirationDate && token.expirationDate !== '') {
          const time = token.expirationTime ? token.expirationTime : '00:00'; // Устанавливаем 00:00, если время не указано
          const localDate = new Date(`${token.expirationDate}T${time}:00`);

          // Преобразуем в UTC
          if (!isNaN(localDate.getTime())) {
            expirationDateTime = localDate.toISOString(); // Конвертация в формат UTC
          }
        }

        // Конвертация TokenType в строковой формат
        let tokenTypeStr;
        switch (token.tokenType) {
          case 'SingleFile':
            tokenTypeStr = 'SingleFile';
            break;
          case 'Folder':
            tokenTypeStr = 'Folder';
            break;
          case 'Admin':
            tokenTypeStr = 'Admin';
            break;
          case 'OneTimeFile':
            tokenTypeStr = 'OneTimeFile';
            break;
          default:
            tokenTypeStr = 'Unknown';
        }

        // Формируем строку запроса с параметрами URL
        const params = new URLSearchParams({
          Hash: token.hash,
          AdminHash: adminHash,
          expirationDate: expirationDateTime,
          tokenType: tokenTypeStr
        });

        // Отправляем запрос с параметрами в URL
        const response = await axios.post(`/tokens/Update?${params.toString()}`, null, {
          headers: {
            'Content-Type': 'application/json',
            accept: '*/*',
          },
        });

        console.log(response);

        token.isEditing = false; // Отключаем режим редактирования после сохранения
        //alert('Token updated successfully');
      } catch (error) {
        console.error('Error updating token:', error.response?.data || error);
        alert('Failed to update token');
      }
    },
    async deleteToken(token) {
      try {
        const axios = await getAxiosInstance();
        const adminHash = this.adminHash;

        // Отправляем запрос на удаление токена
        const response = await axios.delete(`/tokens/Delete?Id=${token.tokenId}&AdminHash=${adminHash}`);
        console.log(response);
        // Удаление успешно, загружаем текущую страницу заново
        this.currentPage = 1; // Сбрасываем текущую страницу
        this.allTokens = []; // Очищаем загруженные токены
        await this.loadTokens(); // Загружаем обновленный список токенов

        //alert('Token deleted successfully');
      } catch (error) {
        console.error('Error deleting token:', error.response?.data || error);
        alert('Failed to delete token');
      }
    },
    copyToClipboard(text, tooltipId) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          const tooltip = document.getElementById(tooltipId);
          tooltip.style.visibility = 'visible';
          setTimeout(() => {
            tooltip.style.visibility = 'hidden';
          }, 1500);
        }).catch(err => {
          console.error('Failed to copy: ', err);
        });
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          const tooltip = document.getElementById(tooltipId);
          tooltip.style.visibility = 'visible';
          setTimeout(() => {
            tooltip.style.visibility = 'hidden';
          }, 1500);
        } catch (err) {
          console.error('Fallback: Oops, unable to copy', err);
        }
        document.body.removeChild(textarea);
      }
    }
  },
  mounted() {
    if (this.isAuthenticated) {
      this.loadTokens(); // Загружаем первую страницу токенов при монтировании компонента
    }
  }
};
</script>

<style scoped>
.copy-box {
  display: inline-block;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  cursor: pointer;
  background-color: #f9f9f9;
  color: black;
  text-decoration: none;
  user-select: none;
  width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.copy-box:hover {
  background-color: #e9e9e9;
}

.tooltip-text {
  visibility: hidden;
  background-color: black;
  color: white;
  text-align: center;
  border-radius: 5px;
  padding: 5px;
  position: absolute;
  z-index: 1;
  margin-left: 10px;
  font-size: 12px;
}
</style>
